import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "components/ui/loader";
import {
  DiwaliCardDecor,
  DiwaliDealLink,
  DiwaliKandils,
  DiwaliPill,
  DiwaliTimer,
  useDiwali,
} from "components/offers/DiwaliDecor";
import type { OfferItem } from "lib/offersData";
import { comboOffers, offersPageHeader, telegramToolsRow } from "lib/offersData";
import { PAGE_STORAGE_KEY } from "lib/theme";

async function detectCountry(): Promise<boolean> {
  try {
    const ipResponse = await fetch("https://api.ipify.org/?format=json");
    const ipData = (await ipResponse.json()) as { ip?: string };

    const countryResponse = await fetch(
      `https://api.ipinfo.io/lite/${ipData.ip}?token=b9e3d19f91a322`,
    );
    const countryData = (await countryResponse.json()) as {
      country_code?: string;
    };

    return countryData.country_code === "IN";
  } catch (error) {
    console.error("Error detecting country:", error);
    return false;
  }
}

function formatPrice(item: OfferItem, useInr: boolean) {
  return useInr ? item.priceInr : item.priceUsd;
}

function OfferComboCard({ item, useInr }: { item: OfferItem; useInr: boolean }) {
  const { active } = useDiwali();
  return (
    <div className={`relative flex w-full min-w-0 flex-col rounded-2xl border border-[#2196f3] bg-[var(--dash-surface)] p-5 shadow-[var(--dash-shadow)] transition-all duration-[250ms] hover:-translate-y-1 hover:border-[var(--dash-border)] hover:shadow-[0_10px_30px_rgba(33,150,243,0.25)] sm:p-8${active ? " dwl-card dwl-card--hero" : ""}`}>
      <DiwaliCardDecor hero />
      <span className="absolute right-3 top-3 z-[3] rounded-lg border border-[rgba(33,150,243,0.4)] bg-[rgba(33,150,243,0.15)] px-2.5 py-1 text-[13px] font-semibold text-[#64b5f6] sm:right-4 sm:top-3.5 sm:text-sm">
        Hot Deal
      </span>
      <div className="mb-4 pr-24 text-[17px] font-medium text-[var(--dash-text-strong)] sm:mb-5 sm:text-[19px]">
        {item.title}
      </div>
      <div className="mb-4 text-[32px] font-extrabold leading-none text-[var(--dash-text-strong)] sm:mb-[18px] sm:text-4xl">
        {formatPrice(item, useInr)}
      </div>
      {item.highlight ? (
        <div className="mb-3 text-[13px] font-semibold leading-snug text-[var(--dash-text-strong)] sm:text-sm">
          {item.highlight}
        </div>
      ) : null}
      <ul className="mb-5 flex-1 list-disc space-y-0.5 pl-5 text-sm leading-7 text-[var(--dash-text)] sm:text-[15px] sm:leading-8">
        {item.features.map((f, i) => (
          <li key={i}>{f}</li>
        ))}
        {item.featureSpacer ? (
          <li className="invisible select-none" aria-hidden>
            Spacer
          </li>
        ) : null}
      </ul>
      <DiwaliDealLink
        href={item.stripeUrl}
        className="block w-full rounded-[10px] bg-[#2196f3] py-3.5 text-center text-base font-semibold text-white no-underline hover:brightness-110 sm:py-[15px] sm:text-[17px]"
      />
    </div>
  );
}

function OfferProductCard({ item, useInr }: { item: OfferItem; useInr: boolean }) {
  const { active } = useDiwali();
  return (
    <div className={`relative flex h-full min-w-0 flex-col rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)] p-5 shadow-[var(--dash-shadow)] transition-all duration-[250ms] hover:-translate-y-1 hover:border-[var(--dash-border)] hover:shadow-[0_10px_30px_rgba(33,150,243,0.25)] sm:p-7${active ? " dwl-card" : ""}`}>
      <DiwaliCardDecor />
      <div className="mb-4 pr-10 text-[17px] font-medium leading-snug text-[var(--dash-text-strong)] sm:text-[19px] lg:min-h-[3.25rem]">
        {item.title}
      </div>
      <div className="mb-4 text-[28px] font-extrabold leading-none text-[var(--dash-text-strong)] sm:text-[32px]">
        {formatPrice(item, useInr)}
      </div>
      <ul className="mb-5 list-disc space-y-0.5 pl-5 text-sm leading-7 text-[var(--dash-text)] sm:text-[15px] sm:leading-8">
        {item.features.map((f, i) => (
          <li key={i}>{f}</li>
        ))}
      </ul>
      <DiwaliDealLink
        href={item.stripeUrl}
        className="mt-auto block w-full rounded-[10px] bg-[#2196f3] py-3.5 text-center text-base font-semibold text-white no-underline hover:brightness-110 sm:py-[15px] sm:text-[17px]"
      />
    </div>
  );
}

export default function TrendingOffers() {
  const navigate = useNavigate();
  const [useInr, setUseInr] = useState(false);
  const [regionReady, setRegionReady] = useState(false);

  useEffect(() => {
    void detectCountry().then((isInr) => {
      setUseInr(isInr);
      setRegionReady(true);
    });
  }, []);

  return (
    <div className="flex h-full w-full min-w-0 max-w-full flex-col overflow-x-clip">
      <div data-diwali-block className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 text-[22px] font-bold text-[var(--dash-text-strong)] sm:text-[28px]">
          <i className="fas fa-fire text-[#ff4757]" aria-hidden />
          Diwali Offers
        </div>
        <button
          type="button"
          onClick={() => {
            localStorage.setItem(PAGE_STORAGE_KEY, "front");
            navigate("/");
          }}
          className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-[10px] border border-[var(--dash-border)] px-4 py-3 text-[15px] text-[var(--dash-text)] transition-all hover:border-[var(--dash-text-strong)] hover:text-[var(--dash-text-strong)] sm:w-auto sm:px-5 sm:text-[17px]"
        >
          <i className="fas fa-arrow-left" aria-hidden />
          Back to Dashboard
        </button>
      </div>

      <div className="min-h-0 min-w-0 max-w-full flex-1 overflow-x-clip overflow-y-auto rounded-xl px-1 py-4 sm:px-5 sm:py-10">
        {!regionReady ? (
          <div
            className="flex min-h-[min(420px,60vh)] flex-col items-center justify-center gap-3"
            role="status"
            aria-live="polite"
            aria-label="Detecting region for pricing"
          >
            <div className="text-[var(--theme-accent)]">
              <Loader />
            </div>
            <p className="text-sm text-[#7a8b9a]">Detecting your region…</p>
          </div>
        ) : (
          <div className="mx-auto w-full">
            <header className="relative mb-8 text-center sm:mb-12 sm:pt-4">
              <DiwaliPill />
              <div className="relative mx-auto w-full max-w-[980px]">
                <DiwaliKandils />
                <h2 className="mb-4 px-1 text-[28px] font-bold leading-tight text-[var(--dash-text-strong)] sm:mb-[18px] sm:text-[36px] xl:px-24 xl:text-[46px]">
                  {offersPageHeader.title}
                </h2>
              </div>
              <p className="mx-auto max-w-[600px] px-1 text-[15px] leading-relaxed text-[#7a8b9a] sm:text-[19px] sm:leading-[1.6]">
                {offersPageHeader.subtitle}
              </p>
              <DiwaliTimer />
            </header>

            <div className="mb-8 w-full max-w-[720px] sm:mb-10">
              {comboOffers.map((item) => (
                <OfferComboCard key={item.id} item={item} useInr={useInr} />
              ))}
            </div>

            <h3 className="mb-5 mt-10 text-[20px] font-semibold text-[var(--dash-text-strong)] sm:mb-6 sm:mt-14 sm:text-[22px]">
              Telegram Tools
            </h3>
            <div className="grid grid-cols-1 items-stretch gap-5 lg:grid-cols-2 xl:grid-cols-3">
              {telegramToolsRow.map((item) => (
                <OfferProductCard key={item.id} item={item} useInr={useInr} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
